import React, { useEffect } from 'react'
import { AgTechWebApplication } from '../../../application/AgTechWebApplication'
import { AgTechButton } from '../../Buttons/AgTechButtons';
import AgTechHeaderDetail from '../../Headers/AgTechHeaderDetail';
import { useModalContext } from '../../Portals/Modals/AgTechModal';
import AgTechLoadingSurface from '../../Pages/Loading/AgTechLoadingSurface';
import AgTechFutureLoadingSurface, { AgTechLoader } from '../../Pages/Loading/AgTechFutureLoadingSurface';
import { AgTechStepModalBody, AgTechStepModalProps } from '../../Portals/Modals/AgTechStepModal';
import AgTechDataActionSurface from '@agtech/core/data/actions/AgTechDataActionSurfaces';

export type RegistrationStep = 'intro' | 'terms' | 'setup' | 'loading';

const AgTechWebAppOperationRegistrationModal = (props: {
    app: AgTechWebApplication,
    targetStep?: RegistrationStep,
    onCompleted: () => void
}) => {
    const modalContext = useModalContext();

    const [currentStep, setCurrentStep] = React.useState<RegistrationStep>('intro');
    const [setupModal, setSetupModal] = React.useState<AgTechStepModalProps | undefined>(undefined);

    const showTermsOfService = () => {
        setCurrentStep('loading');

        modalContext.growModal('640px', '800px')
            .then(() => {
                setCurrentStep('terms');
            });
    }

    const showSetup = async () => {
        setCurrentStep('loading');

        const applicationSetup = props.app.setup;
            
        if (applicationSetup) {
            const applicationSetupModal = applicationSetup.setupModal();

            await modalContext.growModal(
                applicationSetupModal.height,
                applicationSetupModal.width
            );
            
            setSetupModal(applicationSetupModal.modal);
            setCurrentStep('setup');
        }
    }

    const completeSetup = async () => {
        modalContext.closeModal();
        props.onCompleted();
    }

    return (
        <div className='stretched centered row-based flex'>
            {currentStep === 'loading' ? (
                <AgTechLoader text='' />
            )
            : currentStep === 'intro' ? (
                <div className='stretched centered row-based flex'>
                    <AgTechHeaderDetail
                        header={`Welcome to ${props.app.name}!`}
                        detail={
                            props.targetStep === 'terms' ? 'Our terms of service have changed. Please review and accept them to continue'
                            : props.targetStep === 'setup' ? 'Before you begin, please complete our setup process by clicking the button below'
                            : 'Before you begin, please review and accept our terms of service'
                        }
                        settings={{
                            header: {
                                classes: '!text-[42px] text-center'
                            },
                            detail: {
                                classes: 'text-center !text-md mt-2'
                            },
                            classes: 'centered mb-8'
                        }}
                    />
                    {props.targetStep === 'setup' ? (
                        <AgTechButton
                            text='Begin setup'
                            classes='button-highlight mt-8 py-3 px-7 text-plus'
                            action={e => {
                                showSetup();
                            }}
                        />
                    ) : (
                        <AgTechButton
                            text='View terms of service'
                            classes='button-highlight mt-8 py-3 px-7 text-plus'
                            action={e => {
                                showTermsOfService();
                            }}
                        />
                    )}
                </div>
            ) : currentStep === 'terms' ? (
                <AgTechFutureLoadingSurface waitOn={true}>
                    <AgTechDataActionSurface
                        action={props.app.registration!.registrationAction}
                        behavior={{
                            eventHandlers: {
                                onExecutionSuccessful: showSetup
                            }
                        }}
                    >
                        {(executor) => (
                            <div className='stretched items-center row-based py-10 flex'>
                                <h1 className='font-semibold text-md pb-3'>Terms of service</h1>
                                <textarea
                                    value={'Terms of service'}
                                    rows={20}
                                    readOnly={true}
                                    className={'!bg-slate-50 w-[420px] bordered p-3'}
                                    style={{
                                        resize: 'none',
                                    }}
                                />
                                <AgTechButton
                                    text='Accept terms of service'
                                    classes='button-highlight mt-auto py-3 px-5 text-plus'
                                    action={async e => {
                                        await executor.executeAction(props.app.registration!.registrationAction, {
                                            submittedEntity: {}
                                        })
                                    }}
                                />
                            </div>
                        )}
                    </AgTechDataActionSurface>
                </AgTechFutureLoadingSurface>
            ) : (
                <AgTechLoadingSurface waitOn={setupModal !== undefined}>
                    <AgTechDataActionSurface
                        action={props.app.setup!.completeSetupAction}
                        behavior={{
                            eventHandlers: {
                                onExecutionSuccessful: completeSetup
                            }
                        }}
                    >
                        {(executor) => (
                            <AgTechStepModalBody
                                {...setupModal!}
                                onComplete={() => {
                                    executor.executeAction(props.app.setup!.completeSetupAction, {
                                        submittedEntity: {}
                                    })
                                }}
                            />
                        )}
                    </AgTechDataActionSurface>
                </AgTechLoadingSurface>
            )}
        </div>
    )
}

export default AgTechWebAppOperationRegistrationModal